// src/pages/PresentationPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Button,
  Carousel,
  Card,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import "./PresentationPage.css";

const PresentationPage = () => {
  const navigate = useNavigate();

  // ====== Hero slider (imágenes locales) ======
  const sliderItems = [
    {
      id: 1,
      title: "Conectando donantes con quienes necesitan medicamentos",
      description: "Una solución innovadora para reducir el desperdicio de medicamentos",
      image: process.env.PUBLIC_URL + "/images/imagen1.png",
    },
    {
      id: 2,
      title: "Donar medicamentos nunca fue tan fácil",
      description: "Registra tus medicamentos y ayuda a quienes los necesitan",
      image: process.env.PUBLIC_URL + "/images/imagen2.png",
    },
    {
      id: 3,
      title: "Encuentra los medicamentos que necesitas",
      description: "Busca entre nuestro catálogo de medicamentos disponibles",
      image: process.env.PUBLIC_URL + "/images/imagen3.png",
    },
  ];

  // ====== Features (estático) ======
  const features = [
    { title: "Donación Simple", icon: "bi-box2-heart", description: "Publica medicamentos en pocos pasos." },
    { title: "Solicitud Ágil", icon: "bi-bag-plus", description: "Reserva medicamentos con seguimiento de estado." },
    { title: "Seguridad", icon: "bi-shield-check", description: "Validamos datos clave de cada publicación." },
    { title: "Comunidad", icon: "bi-people", description: "Conecta donantes y solicitantes de forma solidaria." },
    { title: "Sostenibilidad", icon: "bi-recycle", description: "Reducimos desperdicio farmacéutico." },
    { title: "Sin Costo", icon: "bi-cash-coin", description: "Servicio gratuito y sin transacciones comerciales." },
    { title: "Notificaciones", icon: "bi-bell", description: "Recibí avisos de novedades y cambios de estado." },
    { title: "Trazabilidad", icon: "bi-clipboard2-pulse", description: "Historial completo de solicitudes y entregas." },
  ];

  // ====== Sponsors (estático) ======
  const sponsors = [
    { name: "FarmaSalud", logo: "https://suriyaco.ar/farmacia/logosSpon/logo1.png" },
    { name: "MediHelp", logo: "https://suriyaco.ar/farmacia/logosSpon/logo2.png" },
    { name: "SaludPlus", logo: "https://suriyaco.ar/farmacia/logosSpon/logo3.png" },
    { name: "Bienestar Farmacéutico", logo: "https://suriyaco.ar/farmacia/logosSpon/logo4.png" },
  ];

  // ====== Estado: medicamentos (destacados) ======
  const [medications, setMedications] = useState([]);
  const [loadingMedications, setLoadingMedications] = useState(true);
  const [medError, setMedError] = useState(null);

  // ====== Estado: noticias (carrusel) ======
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState("");

  const topStats = useMemo(
    () => [
      { label: "Medicamentos activos", value: medications.length, icon: "bi-capsule-pill" },
      { label: "Noticias visibles", value: news.length, icon: "bi-newspaper" },
      { label: "Aliados", value: sponsors.length, icon: "bi-buildings" },
    ],
    [medications.length, news.length, sponsors.length]
  );

  // ---------- Utils ----------
  const fmtDate = (val) => {
    try {
      const d = val?.toDate ? val.toDate() : new Date(val);
      if (Number.isNaN(d?.getTime?.())) return "";
      return d.toLocaleDateString("es-ES", { year: "numeric", month: "short", day: "2-digit" });
    } catch {
      return "";
    }
  };

  const pickTs = (n) => n?.publishedAt || n?.createdAt || null;

  const clip = (text, max = 160) => {
    if (!text) return "";
    const s = String(text).trim();
    return s.length <= max ? s : s.slice(0, max - 1) + "…";
  };

  const resolveNewsImage = async (item) => {
    if (item.imageUrl) return item.imageUrl;
    if (item.imagePath) {
      try {
        return await getDownloadURL(storageRef(storage, item.imagePath));
      } catch {
        return null;
      }
    }
    return null;
  };

  // ---------- Cargar medicamentos (solo algunos para mostrar en portada) ----------
  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const snap = await getDocs(collection(db, "medications"));
        const meds = [];
        snap.forEach((d) => {
          const data = d.data();
          const qty = typeof data.quantity === "number" ? data.quantity : 0;
          if (qty > 0) {
            meds.push({ id: d.id, ...data, quantity: qty });
          }
        });
        setMedications(meds.slice(0, 3)); // mostrar top 3
      } catch (err) {
        console.error("Error al cargar medicamentos:", err);
        setMedError("Error al cargar los medicamentos");
      } finally {
        setLoadingMedications(false);
      }
    };
    fetchMedications();
  }, []);

  // ---------- Cargar noticias (para carrusel) ----------
  useEffect(() => {
    const fetchNews = async () => {
      setNewsLoading(true);
      setNewsError("");
      try {
        const snap = await getDocs(collection(db, "news"));
        let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Solo publicadas (si no hay campo, se asume publicada)
        rows = rows.filter((n) => n.published !== false);

        // Orden: "pinned" primero, luego por fecha (publishedAt/createdAt) desc
        rows.sort((a, b) => {
          const ap = a?.pinned ? 1 : 0;
          const bp = b?.pinned ? 1 : 0;
          if (ap !== bp) return bp - ap;
          const ta = pickTs(a);
          const tb = pickTs(b);
          const da = ta?.toDate ? ta.toDate().getTime() : new Date(ta || 0).getTime();
          const dbb = tb?.toDate ? tb.toDate().getTime() : new Date(tb || 0).getTime();
          return dbb - da;
        });

        // Limitar a 5 ítems en el carrusel
        rows = rows.slice(0, 5);

        // Resolver imagen (imageUrl o imagePath en Storage)
        const withImgs = await Promise.all(
          rows.map(async (n) => ({
            ...n,
            _image: await resolveNewsImage(n),
          }))
        );

        setNews(withImgs);
      } catch (e) {
        console.error("Error cargando noticias:", e);
        setNewsError("No se pudieron cargar las noticias.");
      } finally {
        setNewsLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="presentation-page">
      {/* ===== Hero Slider ===== */}
      <section className="hero-slider mb-5">
        <Carousel fade indicators={false}>
          {sliderItems.map((item) => (
            <Carousel.Item key={item.id}>
              <div className="slider-item-container">
                <div
                  className="slider-image"
                  style={{
                    backgroundImage: `url(${item.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div className="slider-content">
                  <Container>
                    <Badge bg="light" text="dark" pill className="hero-chip mb-3">
                      <i className="bi bi-heart-pulse me-2" />
                      Plataforma solidaria
                    </Badge>
                    <h2 className="slider-title">{item.title}</h2>
                    <p className="slider-description">{item.description}</p>
                    <div className="slider-buttons">
                      <Button as={Link} to="/login" variant="warning" size="lg" className="me-3">
                        <i className="bi bi-person-plus me-2" />
                        Regístrate ahora
                      </Button>
                      <Button as={Link} to="/about" variant="outline-light" size="lg">
                        <i className="bi bi-info-circle me-2" />
                        Conoce más
                      </Button>
                    </div>
                  </Container>
                </div>
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      </section>

      <section className="stats-strip mb-5">
        <Container>
          <Row className="g-3">
            {topStats.map((s) => (
              <Col md={4} key={s.label}>
                <Card className="border-0 shadow-sm h-100 stats-card">
                  <Card.Body className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-muted small">{s.label}</div>
                      <div className="fs-4 fw-bold">{s.value}</div>
                    </div>
                    <div className="stats-icon-wrap">
                      <i className={`bi ${s.icon}`} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== Features ===== */}
      <section className="features-section py-5 bg-light">
        <Container>
          <h2 className="text-center mb-5 text-primary">¿Cómo Funciona Nuestra Plataforma?</h2>
          <Row xs={1} md={2} lg={3} className="g-4">
            {features.map((feature, idx) => (
              <Col key={idx}>
                <Card className="h-100 feature-card border-0 shadow-sm">
                  <Card.Body className="text-center py-4">
                    <div className="feature-icon mb-3">
                      <i className={`bi ${feature.icon}`} />
                    </div>
                    <Card.Title>{feature.title}</Card.Title>
                    <Card.Text>{feature.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== Medicamentos Destacados ===== */}
      <section className="medications-section py-5">
        <Container>
          <h2 className="text-center mb-5 text-primary">Medicamentos Disponibles</h2>

          {loadingMedications ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
            </div>
          ) : medError ? (
            <Alert variant="danger" className="text-center">
              {medError}
            </Alert>
          ) : medications.length === 0 ? (
            <p className="text-center">No hay medicamentos disponibles en este momento.</p>
          ) : (
            <>
              <Row xs={1} md={3} className="g-4">
                {medications.map((med) => (
                  <Col key={med.id}>
                    <Card className="h-100 medication-card">
                      <Card.Img
                        variant="top"
                        src={
                          med.imageUrl ||
                          "https://via.placeholder.com/300x200?text=Medicamento"
                        }
                        style={{ height: "200px", objectFit: "cover" }}
                      />
                      <Card.Body>
                        <Card.Title>{med.name}</Card.Title>
                        <Card.Text>
                          <strong>Dosis:</strong> {med.dosage || "No especificada"}
                          <br />
                          <strong>Categoría:</strong> {med.category || "No especificada"}
                          <br />
                          {med.description && (
                            <>
                              <strong>Descripción:</strong>{" "}
                              {String(med.description).substring(0, 60)}…
                            </>
                          )}
                        </Card.Text>
                        {med.quantity > 0 ? (
                          <Badge
                            bg={med.quantity > 5 ? "success" : "warning"}
                            className="mb-2"
                          >
                            {med.quantity > 5 ? "En Stock" : "Últimas unidades"}
                          </Badge>
                        ) : (
                          <Badge bg="danger" className="mb-2">
                            Sin Stock
                          </Badge>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <div className="text-center mt-4">
                <Button as={Link} to="/home" variant="primary" size="lg">
                  <i className="bi bi-grid me-2" />
                  Ver catálogo completo
                </Button>
              </div>
            </>
          )}
        </Container>
      </section>

      {/* ===== Últimas Noticias (Carrusel desde Firestore) ===== */}
      <section className="news-section py-5 bg-light">
        <Container>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0 text-primary">Últimas Noticias</h2>
            <Button variant="outline-primary" size="sm" onClick={() => navigate("/noticias")}>
              <i className="bi bi-arrow-right-circle me-2" />
              Ver todas
            </Button>
          </div>

          {newsError && (
            <Alert
              variant="warning"
              onClose={() => setNewsError("")}
              dismissible
              className="mb-3"
            >
              {newsError}
            </Alert>
          )}

          {newsLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Cargando noticias...</span>
              </Spinner>
            </div>
          ) : news.length === 0 ? (
            <Card className="mb-4">
              <Card.Body className="text-center text-muted">
                Aún no hay noticias publicadas.
              </Card.Body>
            </Card>
          ) : (
            <Carousel
              interval={6000}
              controls={true}
              indicators={news.length > 1}
              className="shadow-sm"
            >
              {news.map((n) => {
                const title = n.title || "Sin título";
                const summary = n.summary || n.subtitle || n.content || "";
                const when = pickTs(n);
                return (
                  <Carousel.Item key={n.id}>
                    {n._image ? (
                      <div
                        style={{
                          width: "100%",
                          height: 300,
                          overflow: "hidden",
                          background: "#f8f9fa",
                        }}
                      >
                        <img
                          src={n._image}
                          alt={title}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: 300,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "linear-gradient(135deg, #f8f9fa, #e9ecef)",
                        }}
                      >
                        <span className="text-muted">Sin imagen</span>
                      </div>
                    )}

                    <Carousel.Caption
                      className="text-start"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.65) 60%, rgba(0,0,0,0.8) 100%)",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: "1rem 1.25rem",
                      }}
                    >
                      <h5 className="text-white mb-1">{title}</h5>
                      <div className="text-light-50 small mb-2">
                        {when ? fmtDate(when) : ""}
                      </div>
                      {!!summary && (
                        <p className="text-white-50 mb-2">{clip(summary, 140)}</p>
                      )}
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => navigate(`/noticias`)}
                      >
                        Leer más
                      </Button>
                    </Carousel.Caption>
                  </Carousel.Item>
                );
              })}
            </Carousel>
          )}
        </Container>
      </section>

      {/* ===== Sponsors ===== */}
      <section className="sponsors-section py-5">
        <Container>
          <h2 className="text-center mb-5 text-primary">Nuestros Aliados</h2>
          <Row className="align-items-center justify-content-center g-4">
            {sponsors.map((s, idx) => (
              <Col
                xs={6}
                md={3}
                key={idx}
                className="text-center d-flex align-items-center justify-content-center"
              >
                <img src={s.logo} alt={s.name} className="img-fluid sponsor-logo" />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-section py-5 bg-primary text-white">
        <Container className="text-center">
          <h2 className="mb-4">¿Listo para unirte a nuestra comunidad?</h2>
          <p className="lead mb-4">
            Regístrate ahora y comienza a donar o recibir medicamentos hoy mismo.
          </p>
          <Button as={Link} to="/login" variant="warning" size="lg" className="me-3">
            <i className="bi bi-person-plus me-2" />
            Regístrate gratis
          </Button>
          <Button as={Link} to="/about" variant="outline-light" size="lg">
            <i className="bi bi-info-circle me-2" />
            Más información
          </Button>
        </Container>
      </section>
    </div>
  );
};

export default PresentationPage;
